/**
 * 类型化 axios 客户端 + 拦截器（复刻 ops-console `src/api/client.ts` 语义，调研§2）。
 *
 * 约定（后端不可改，story Dev Notes 调研§7）:
 *  - envelope: `{ code, message, data, traceId }`，成功 code === "000000"，业务失败仍 HTTP 200。
 *  - 401（HTTP status）携带 010100（未登录）/ 010105（token 撤销）→ 清会话 + 跳登录。
 *  - 认证路径需合成 `X-Device-Fingerprint`（64-hex，调研§4）。
 *
 * baseURL 留空 → 走同源相对路径，由 nginx 同域反代（生产）/ vite proxy（dev）转后端，规避 CORS。
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/store/auth';
import { getDeviceFingerprint } from '@/lib/deviceFingerprint';

export const SUCCESS_CODE = '000000';
const BASE_URL = import.meta.env.VITE_API_BASE ?? '';
const FINGERPRINT_HEADER = 'X-Device-Fingerprint';
const LOGIN_PATH = '/login';

export interface Envelope<T> {
  code: string;
  message: string;
  data: T;
  traceId?: string;
}

/** 业务错误：envelope.code !== "000000"（HTTP 仍可能是 200）。 */
export class ServerError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly traceId?: string,
  ) {
    super(message);
    this.name = 'ServerError';
  }
}

/** 认证路径前缀（与后端 DeviceFingerprintFilter 覆盖一致，调研§4）。 */
function requiresFingerprint(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/api/v1/auth/register/') ||
    url.includes('/api/v1/auth/sms/send') ||
    url.includes('/api/v1/auth/login/')
  );
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
});

// ---- 请求拦截:注入 Bearer token + 认证路径合成设备指纹 ----
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // 仅在会话有效(token 存在且未过期)时注入:否则过期 token 会被后端资源服务器过滤器
  // 以 401 拒掉——即使是 permitAll 的 sms/send——表现为"首次点获取验证码报未登录、
  // 401 清会话后二次才发得出"。过期即不带,公共端点走匿名。
  const auth = useAuthStore.getState();
  const token = auth.accessToken;
  if (token && auth.isAuthenticated() && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (requiresFingerprint(config.url)) {
    try {
      config.headers[FINGERPRINT_HEADER] = await getDeviceFingerprint();
    } catch {
      // 指纹合成失败则不带:后端返 090101,UI 走错误态（不静默吞）。
    }
  }
  return config;
});

// ---- 响应拦截:解 envelope + 业务码 + 401 ----
apiClient.interceptors.response.use(
  (resp: AxiosResponse) => {
    const env = resp.data as Envelope<unknown> | undefined;
    // 非 envelope(文件流等)直接透传。
    if (!env || typeof env !== 'object' || !('code' in env)) return resp;
    if (env.code !== SUCCESS_CODE) {
      throw new ServerError(env.code, env.message ?? '请求失败', env.traceId);
    }
    return resp;
  },
  (error: AxiosError) => {
    // 401:以 HTTP status 为准(010100 未登录 / 010105 撤销 均走 401)。
    // 带 ?expired=1 → 登录页提示「登录已过期,请重新登录」(login.md 会话过期边界 / AC-10)。
    if (error.response?.status === 401) {
      useAuthStore.getState().clear();
      if (typeof window !== 'undefined' && window.location.pathname !== LOGIN_PATH) {
        window.location.replace(`${LOGIN_PATH}?expired=1`);
      }
    }
    // 非 2xx 但带 envelope:抛 ServerError 让上层拿到业务码。
    const data = error.response?.data;
    if (data && typeof data === 'object' && 'code' in data) {
      const env = data as Envelope<unknown>;
      throw new ServerError(env.code, env.message ?? '请求失败', env.traceId);
    }
    throw error;
  },
);

/** 发请求并解包 envelope.data。业务失败抛 ServerError(带 code)。 */
export async function call<T>(config: Parameters<AxiosInstance['request']>[0]): Promise<T> {
  const resp = await apiClient.request<Envelope<T>>(config);
  return resp.data.data;
}
